import { t as globalT } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  Badge,
  Breadcrumbs,
  Group,
  Input,
  Select,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { useQuery } from "@supabase-cache-helpers/postgrest-react-query";
import { LoadingSuspense } from "@sweco-se/reality-frontend";
import { createFileRoute } from "@tanstack/react-router";
import { type FC, useMemo, useState } from "react";

import { api } from "@/api.ts";
import { useSuspenseQueryPostgrest } from "@/hooks/useSuspenseQueryPostgrest";

export const Route = createFileRoute("/admin/sources/")({
  component: RouteComponent,
  head: () => ({
    meta: [
      {
        title: globalT`Sweco REALity` + " - " + globalT`Sources`,
      },
    ],
  }),
});

type Variant =
  | "internal_geodatasamverkan"
  | "internal_other"
  | "external_geodatasamverkan"
  | "external_other";

interface Filters {
  reader: string;
  hasCost: string | null;
  variant: Variant | null;
  customerId: string | null;
}

function getVariant(d: {
  internal_geodatasamverkan: boolean;
  internal_other: boolean;
  external_geodatasamverkan: boolean;
  external_other: boolean;
}): Variant | null {
  if (d.internal_geodatasamverkan) return "internal_geodatasamverkan";
  if (d.internal_other) return "internal_other";
  if (d.external_geodatasamverkan) return "external_geodatasamverkan";
  if (d.external_other) return "external_other";
  return null;
}

const Sources: FC<{ filters: Filters }> = ({ filters }) => {
  const { data } = useSuspenseQueryPostgrest(
    useMemo(() => {
      let q = api
        .from("source")
        .select(
          "reader,base_url,project_id,customer_id,customer(name),has_cost,internal_geodatasamverkan,internal_other,external_geodatasamverkan,external_other",
        );
      if (filters.reader) {
        q = q.ilike("reader", `%${filters.reader}%`);
      }
      if (filters.hasCost) {
        q = q.eq("has_cost", filters.hasCost === "yes");
      }
      if (filters.variant) {
        q = q.eq(filters.variant, true);
      }
      if (filters.customerId) {
        q = q.eq("customer_id", Number(filters.customerId));
      }
      return q;
    }, [filters]),
  );

  return (
    <Table>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>
            <Trans>Reader</Trans>
          </Table.Th>
          <Table.Th>
            <Trans>Base URL</Trans>
          </Table.Th>
          <Table.Th>
            <Trans>Project</Trans>
          </Table.Th>
          <Table.Th>
            <Trans>Customer</Trans>
          </Table.Th>
          <Table.Th>
            <Trans>Has cost</Trans>
          </Table.Th>
          <Table.Th>
            <Trans>Variant</Trans>
          </Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {data.map((d, index) => {
          const variant = getVariant(d);
          return (
            <Table.Tr key={index}>
              <Table.Td>{d.reader}</Table.Td>
              <Table.Td>{d.base_url}</Table.Td>
              <Table.Td>{d.project_id ?? "-"}</Table.Td>
              <Table.Td>{d.customer?.name ?? "-"}</Table.Td>
              <Table.Td>
                <Badge color={d.has_cost ? "sand" : "gray"} variant="light">
                  {d.has_cost ? <Trans>Yes</Trans> : <Trans>No</Trans>}
                </Badge>
              </Table.Td>
              <Table.Td>
                {variant ? (
                  <Badge
                    color={variant.startsWith("internal") ? "green" : "blue"}
                    variant="light"
                  >
                    {variant === "internal_geodatasamverkan" ? (
                      <>
                        <Trans context="user category">Internal</Trans> ·{" "}
                        <Trans context="customer category">
                          Geodatasamverkan
                        </Trans>
                      </>
                    ) : variant === "internal_other" ? (
                      <>
                        <Trans context="user category">Internal</Trans> ·{" "}
                        <Trans context="customer category">Other</Trans>
                      </>
                    ) : variant === "external_geodatasamverkan" ? (
                      <>
                        <Trans context="user category">External</Trans> ·{" "}
                        <Trans context="customer category">
                          Geodatasamverkan
                        </Trans>
                      </>
                    ) : (
                      <>
                        <Trans context="user category">External</Trans> ·{" "}
                        <Trans context="customer category">Other</Trans>
                      </>
                    )}
                  </Badge>
                ) : (
                  <Text fz="sm" c="dimmed">
                    -
                  </Text>
                )}
              </Table.Td>
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );
};

function RouteComponent() {
  const { t } = useLingui();
  const [filters, setFilters] = useState<Filters>({
    reader: "",
    hasCost: null,
    variant: null,
    customerId: null,
  });

  const { data: customers } = useQuery(
    useMemo(() => api.from("customer").select("id,name").order("name"), []),
  );

  return (
    <Stack gap="lg">
      <Breadcrumbs>
        <Text>
          <Trans>Admin</Trans>
        </Text>
        <Text>
          <Trans>Sources</Trans>
        </Text>
      </Breadcrumbs>

      <Group>
        <Input
          value={filters.reader}
          onChange={(e) =>
            setFilters((f) => ({ ...f, reader: e.target.value }))
          }
          placeholder={t`Search reader...`}
        />
        <Select
          placeholder={t`Has cost`}
          data={[
            { value: "yes", label: t`Yes` },
            { value: "no", label: t`No` },
          ]}
          value={filters.hasCost}
          onChange={(value) => setFilters((f) => ({ ...f, hasCost: value }))}
          clearable
        />
        <Select
          placeholder={t`Variant`}
          data={[
            {
              value: "internal_geodatasamverkan",
              label:
                t({ message: "Internal", context: "user category" }) +
                " · " +
                t({
                  message: "Geodatasamverkan",
                  context: "customer category",
                }),
            },
            {
              value: "internal_other",
              label:
                t({ message: "Internal", context: "user category" }) +
                " · " +
                t({ message: "Other", context: "customer category" }),
            },
            {
              value: "external_geodatasamverkan",
              label:
                t({ message: "External", context: "user category" }) +
                " · " +
                t({
                  message: "Geodatasamverkan",
                  context: "customer category",
                }),
            },
            {
              value: "external_other",
              label:
                t({ message: "External", context: "user category" }) +
                " · " +
                t({ message: "Other", context: "customer category" }),
            },
          ]}
          value={filters.variant}
          onChange={(value) =>
            setFilters((f) => ({ ...f, variant: value as Variant | null }))
          }
          clearable
        />
        <Select
          placeholder={t`Customer`}
          data={(customers ?? []).map((c) => ({
            value: String(c.id),
            label: c.name,
          }))}
          value={filters.customerId}
          onChange={(value) => setFilters((f) => ({ ...f, customerId: value }))}
          clearable
          searchable
        />
      </Group>
      <LoadingSuspense>
        <Sources filters={filters} />
      </LoadingSuspense>
    </Stack>
  );
}
